import type Luminary from '@lumen5/luminary';
import type { GetRootState } from 'js/src/libs/RootState';

import type { LintFinding } from '../videoLinter';

import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { calculateAllTextContrastsForVideo } from 'js/src/luminary/text-contrast-utils';
import { meetsWCAG_AA, wcagAAThreshold } from 'js/src/utils/contrast-utils';

/**
 * Error: text overlapping media has a contrast ratio below the WCAG AA
 * threshold, making it hard to read against its background. The threshold
 * depends on the text size: 3:1 for large text, 4.5:1 for normal text.
 */
export async function checkTextContrast(
    luminary: Luminary,
    _getState: GetRootState,
): Promise<LintFinding[]> {
    const findings: LintFinding[] = [];
    const canvas = document.createElement('canvas');

    const resultsBySceneId = await calculateAllTextContrastsForVideo(luminary, canvas);

    luminary.scenes.forEach((scene) => {
        const results = resultsBySceneId.get(scene.id);
        if (!results) {
            return;
        }
        results.forEach((result) => {
            if (meetsWCAG_AA(result.contrastRatio, result.isLargeText)) {
                return;
            }
            const ratio = result.contrastRatio.toFixed(1);
            const threshold = wcagAAThreshold(result.isLargeText);
            const tier = result.isLargeText ? 'large text' : 'normal-size text';
            const text = result.textContent.trim();
            findings.push({
                check: 'text_contrast',
                severity: 'error',
                sceneId: scene.id,
                message: `Low text contrast (${ratio}:1, below the ${threshold}:1 minimum for ${tier})${
                    text ? ` for "${text}"` : ''
                }. Load the ${SKILL_NAMES.fixTextContrast} skill for the fix procedure.`,
            });
        });
    });

    return findings;
}
